import rpcService, { withLocalePathSegment } from '@/base/rpc/rpc-service';
import state from '@/base/state';
import {
    DownOutlined,
    FileMarkdownFilled,
    FileMarkdownOutlined, RightOutlined
} from '@ant-design/icons';
import { Spin, Tree } from 'antd';
import { DataNode } from 'antd/lib/tree';
import { TreeNodeProps } from 'rc-tree/lib/TreeNode';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HOME_PAGE } from '../configs';
import { fetchChildren } from '../services/doc-service';
import { setEdition } from '../stores/doc-store';
import '../styles/doc.less';
import { DocumentNodeType, IDocumentOutline, IDocumentsResponse } from '../types/doc-types';
import { getEditionFromParams, getEditionInfoFromParams } from '../utils/edition';
import { getBasePathFromParam } from '../utils/urls';

type DocDataNode = DataNode & {model: IDocumentOutline};

const getChildren = async (query: {key?: string, edition: string}): Promise<DocDataNode[]> => {
    const res = await fetchChildren(query);
    // HOME_PAGE要在导航节点中隐藏掉
    return res.data.filter(doc => doc.Key !== HOME_PAGE).map((doc) => {
        const key = doc.Key;
        return {
            title: doc.Title,
            key,
            isLeaf: doc.NodeType === DocumentNodeType.PureContent,
            switcherIcon(props: TreeNodeProps<DataNode>) {
                const data = props.data as DocDataNode;
                const model = data.model;
                return model.NodeType === DocumentNodeType.PureContent ? (
                    props.selected ? <FileMarkdownFilled /> : <FileMarkdownOutlined />
                ) : props.expanded ? (
                    <DownOutlined />
                ) : (
                    <RightOutlined />
                );
            },
            model: doc,
        }
    }).sort((a, b) => a.model.Order - b.model.Order);
}

const getPath = async (query: {key?: string, edition: string}): Promise<IDocumentOutline[]> => {
    const res = await rpcService.getJSON<IDocumentsResponse>(`/${query.edition}/${withLocalePathSegment(query.edition, state.locale)}doc-path`, query);
    return res.data;
}

const DocNav: React.FC = () => {

    const navigate = useNavigate();

    const params = useParams();

    const loc = useLocation();

    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(setEdition(getEditionInfoFromParams(params)));
    }, [params])

    const [treeNodes, setTreeNodes] = React.useState<Map<React.Key, {isPrimary: boolean; data: DocDataNode}>>();

    const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = React.useState<boolean>(true);
    const [selectedKeys, setSelectedKeys] = React.useState<React.Key[]>([]);

    React.useEffect(() => {
        (async () => {
            const key = params['*'];
            const expandedKeys: React.Key[] = [];
            const m = new Map(treeNodes);
            if (! treeNodes?.size) {
                const children = await getChildren({edition: getEditionFromParams(params)});
                children.forEach(c => {
                    m.set(c.key, {isPrimary: true, data: c});
                });
            }
            if (key) {
                expandedKeys.includes(key) || expandedKeys.push(key);
                const query = {key, edition: getEditionFromParams(params)}
                const path = (await getPath(query)).reverse();
                let _children = Array.from(m?.values() || []).filter(node => node.isPrimary).map(node => node.data);
                for (const docOutline of path) {
                    const p = _children.find(c => c.model.Key === docOutline.Key);
                    if (p) {
                        expandedKeys.includes(p.key) || expandedKeys.push(p.key);
                        if (! p.children) {
                            _children = await getChildren({key: docOutline.Key, edition: getEditionFromParams(params)});
                            p.children = _children;
                        } else {
                            _children = p.children as DocDataNode[];
                        }
                        _children.forEach(c => {
                            m.has(c.key) || m.set(c.key, {isPrimary: false, data: c});
                        });
                    } else {
                        break;
                    }
                }

                setExpandedKeys(prev => [...prev.filter(p => ! expandedKeys.includes(p)), ...expandedKeys]);
                setSelectedKeys([key]);
                setAutoExpandParent(false);
                setTreeNodes(m);
            }
        })();
    }, [loc.pathname])

    const onLoadData = ({ key, children }: any) =>
        new Promise<void>(resolve => {
            if (children) {
                resolve();
            } else {
                getChildren({key, edition: getEditionFromParams(params)}).then(children => {
                    setTreeNodes(prev => {
                        const m = new Map(prev);
                        const node = m.get(key);
                        if (node) {
                            node.data.children = children;
                            children.forEach(c => {
                                m.set(c.key, {isPrimary: false, data: c});
                            });
                        }
                        console.log({nodes: m});
                        return m;
                    });
                    setTimeout(resolve, 0);
                })
            }
        });

    return treeNodes?.size ? (
        <Tree.DirectoryTree
            className='doc-nav-tree'
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            expandAction={false}
            showIcon={false}
            selectedKeys={selectedKeys}
            onExpand={expandedKeys => {
                setExpandedKeys(expandedKeys);
            }}
            loadData={onLoadData}
            treeData={Array.from(treeNodes.values())
                .filter((c) => c.isPrimary)
                .map((c) => c.data)}
            showLine={true}
            onSelect={(keys, e) => {
                if (keys?.[0]) {
                    navigate(`${getBasePathFromParam(params)}/d/${keys[0]}`);
                }
            }}
        />
    ) : (
        <Spin />
    );
};

export default DocNav;
